import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell, Bookmark, Bot, CalendarCheck, Crown, Gift, Heart, KanbanSquare, LineChart, LogOut, Menu, Search, ShieldCheck, Sparkles, User,
  FilePen, Headset, Users,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { TierBadge } from "@/components/Common";
import { Footer } from "@/components/Footer";
import { AutoRecheck } from "@/components/AutoRecheck";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { openTour } from "@/lib/onboarding";
import { PlayCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { WbDot, WbHeartPill, useWbPending } from "@/components/WbNav";
import { BrandLogo } from "@/components/BrandLogo";
import { ClientLayout } from "@/components/ClientLayout";

const NAV = [
  { to: "/search", label: "Search", icon: Search },
  { to: "/people-finder", label: "People Finder", icon: Users },
  { to: "/wellbeing", label: "Well-Being", icon: Heart, wb: true },
  { to: "/saved", label: "Saved", icon: Bookmark },
  { to: "/tracker", label: "Tracker", icon: KanbanSquare },
  { to: "/ai-studio", label: "AI Studio", icon: Sparkles },
  { to: "/resume-studio", label: "Resume Studio", icon: FilePen },
  { to: "/recruiter", label: "Talk to a Recruiter", icon: Headset },
  { to: "/profile", label: "Profile", icon: User },
];
const MORE = [
  { to: "/auto-apply", label: "Auto-Apply", icon: Bot },
  { to: "/alerts", label: "Job Alerts", icon: Bell },
  { to: "/calendar", label: "Weekly Plan", icon: CalendarCheck },
  { to: "/insights", label: "Insights", icon: LineChart },
  { to: "/referrals", label: "Referrals", icon: Gift },
  { to: "/upgrade", label: "Upgrade", icon: Crown },
];

export const Logo = () => (
  <Link to="/search" className="flex items-center" data-testid="logo-link">
    <BrandLogo size="sm" testid="brand-logo" />
  </Link>
);

const isWbPath = (p) => p.startsWith("/wellbeing");
const MAIN_PATHS = ["/search", "/ai-studio", "/tracker", "/profile", "/saved", "/resume-studio"];

const WbNavItem = ({ item, onClick }) => {
  const pending = useWbPending();
  const { pathname } = useLocation();
  {
    const active = isWbPath(pathname);
    return (
      <NavLink to={item.to} end onClick={onClick} data-testid="nav-wellbeing"
        className={cn("group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          active ? "bg-[#33290D] text-[#F3C649]" : "text-[#D4AF37] hover:bg-[#33290D]/60")}>
        <Heart className="h-4 w-4" fill={active ? "currentColor" : "none"} />
        {item.label}
        {pending && <WbDot className="ml-auto" />}
      </NavLink>
    );
  }
};

const NavItem = ({ item, onClick }) => {
  if (item.wb) return <WbNavItem item={item} onClick={onClick} />;
  return (
    <NavLink to={item.to} onClick={onClick} data-testid={`nav-${item.to.slice(1)}`}
      className={({ isActive }) => cn(
        "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
        isActive ? "bg-[#D4AF37]/10 text-[#F3C649]" : "text-zinc-400 hover:bg-[#18181B] hover:text-zinc-100"
      )}>
      <item.icon className="h-4 w-4" />
      {item.label}
    </NavLink>
  );
};

const BOTTOM = [
  { to: "/search", label: "Search", icon: Search },
  { to: "/people-finder", label: "People", icon: Users },
  { to: "/wellbeing", label: "Well-Being", icon: Heart, wb: true },
  { to: "/tracker", label: "Tracker", icon: KanbanSquare },
  { to: "/ai-studio", label: "Studio", icon: Sparkles },
];

const BottomBar = () => {
  const pending = useWbPending();
  const { pathname } = useLocation();
  return (
    <nav className="fixed inset-x-0 bottom-0 z-30 flex items-end justify-around border-t border-[#27272A] bg-[#09090B]/95 px-2 pb-[max(env(safe-area-inset-bottom),6px)] pt-1.5 backdrop-blur-md lg:hidden" data-testid="mobile-bottom-bar">
      {BOTTOM.map((i) => {
        const active = i.wb ? isWbPath(pathname) : pathname.startsWith(i.to);
        if (i.wb) return (
          <Link key={i.to} to={i.to} className="relative -mt-5 flex flex-col items-center gap-0.5" data-testid="bottom-nav-wellbeing">
            <span className={cn("flex h-12 w-12 items-center justify-center rounded-full border-2 transition-colors", active ? "border-[#D4AF37] bg-[#D4AF37] text-[#09090B]" : "border-[#D4AF37]/60 bg-[#33290D] text-[#F3C649]")}>
              <Heart className="h-5 w-5" fill={active ? "currentColor" : "none"} />
            </span>
            {pending && <WbDot className="absolute right-0.5 top-0.5" />}
            <span className="text-[10px] font-medium text-[#F3C649]">{i.label}</span>
          </Link>
        );
        return (
          <Link key={i.to} to={i.to} className={cn("flex flex-col items-center gap-0.5 px-2 py-1 text-[10px]", active ? "text-[#F3C649]" : "text-zinc-500")} data-testid={`bottom-nav-${i.to.slice(1)}`}>
            <i.icon className="h-5 w-5" />{i.label}
          </Link>
        );
      })}
    </nav>
  );
};

const SidebarBody = ({ onNav }) => {
  const { isAdmin, adminAreas, user, tier, logout } = useAuth();
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5"><Logo /></div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3">
        {NAV.map((i) => <NavItem key={i.to} item={i} onClick={onNav} />)}
        <div className="px-3 pb-2 pt-6 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-600">Command</div>
        {MORE.map((i) => <NavItem key={i.to} item={i} onClick={onNav} />)}
        {isAdmin && <NavItem item={{ to: "/admin/cc", label: "Command Center", icon: ShieldCheck }} onClick={onNav} />}
        {!isAdmin && adminAreas.includes("rcc") && <NavItem item={{ to: "/admin/rcc", label: "Revenue Command Center", icon: ShieldCheck }} onClick={onNav} />}
        {!isAdmin && adminAreas.includes("si") && <NavItem item={{ to: "/admin/si", label: "Sourcing Intelligence", icon: ShieldCheck }} onClick={onNav} />}
        <button onClick={() => { onNav?.(); openTour("spotlight"); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-zinc-500 transition-colors hover:bg-[#18181B] hover:text-[#F3C649]" data-testid="nav-how-it-works"><PlayCircle className="h-4 w-4" />How it works</button>
      </nav>
      <div className="m-3 flex items-center gap-3 rounded-xl border border-[#27272A] bg-[#18181B] p-3">
        {user?.picture ? <img src={user.picture} alt="" className="h-9 w-9 rounded-full" /> : <div className="h-9 w-9 rounded-full bg-[#27272A]" />}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm text-zinc-200" data-testid="sidebar-user-name">{user?.name}</div>
          <TierBadge tier={tier} />
        </div>
        <button onClick={logout} className="text-zinc-500 transition-colors hover:text-[#D4AF37]" data-testid="logout-button" title="Log out">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const TrialPill = () => {
  const { billing } = useAuth();
  if (!billing?.trial_active) return null;
  const ends = billing.trial_ends_at ? new Date(billing.trial_ends_at) : null;
  const days = ends ? Math.max(0, Math.ceil((ends - Date.now()) / 864e5)) : null;
  return (
    <Link to="/upgrade" data-testid="trial-pill"
      className="hidden items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs font-medium text-[#F3C649] transition-colors hover:bg-[#D4AF37]/20 sm:flex">
      <Crown className="h-3.5 w-3.5" /> Elite trial{days != null && ` · ${days}d left`}
    </Link>
  );
};

export default function Layout() {
  const { client } = useAuth();
  if (client) return <ClientLayout />;
  return <MemberLayout />;
}

function MemberLayout() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const h = (e) => {
      toast.message(e.detail?.message || "This feature needs an upgrade");
      navigate("/upgrade", { state: { reason: e.detail } });
    };
    window.addEventListener("rpopro:upgrade", h);
    return () => window.removeEventListener("rpopro:upgrade", h);
  }, [navigate]);

  return (
    <div className="grain min-h-screen bg-[#09090B]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-[#27272A] bg-[#09090B] lg:block">
        <SidebarBody />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-[#27272A] bg-[#09090B] p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarBody onNav={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-[#27272A] bg-[#09090B]/80 px-4 backdrop-blur-md md:px-8">
          <div className="flex items-center gap-3">
            <button className="text-zinc-300 lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
            <div className="lg:hidden"><Logo /></div>
            <span className="hidden font-mono text-xs uppercase tracking-widest text-zinc-500 lg:block">{location.pathname.split("/")[1]?.replace("-", " ")}</span>
          </div>
          <div className="flex items-center gap-2">
            <TrialPill />
            {MAIN_PATHS.some((p) => location.pathname.startsWith(p)) && <WbHeartPill />}
          </div>
        </header>
        <main className="relative z-[3] mx-auto max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
          <ErrorBoundary key={location.pathname}><Outlet /></ErrorBoundary>
        </main>
        <div className="pb-20 lg:pb-0"><Footer /></div>
        <BottomBar />
        <AutoRecheck />
      </div>
    </div>
  );
}
